import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { AdSetEntry } from '../api/getAdSets';
import type { FieldValue, UseFormReturn } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { ChangeEvent, MouseEvent } from 'react';
import { type FC, memo, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, InputAdornment, Typography } from '@mui/material';
import { TextField } from 'components/common';
import { CopyAll } from '@mui/icons-material';
import { useInView } from 'react-intersection-observer';
import { isUndefined } from 'lodash-es';
import { parseFlowsString } from 'utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface FormData extends FieldValue<any> {
  selectedAdSets: AdSetEntry[];
  selectedAdSetsBudgets: Record<string, string>;
  selectedCampaignsBudgets: Record<string, string>;
}

interface AdSetBudgetFieldProps {
  cellParams: GridRenderCellParams<AdSetEntry>;
}

interface UseAdSetBudgetForm extends Pick<UseFormReturn<FormData>, 'setValue' | 'getValues'> {
  selectedAdSets: AdSetEntry[];
  currentRow: AdSetEntry | undefined;
  isSelected: boolean;
  isCBO: boolean;
  isABO: boolean;
  ABO_AdSetIds: string[];
  fieldName: string;
}

const useAdSetBudgetForm = (currentId: string): UseAdSetBudgetForm => {
  const { watch, setValue, getValues } = useFormContext<FormData>();

  const selectedAdSets = watch('selectedAdSets');

  const currentRow = useMemo(
    () => selectedAdSets?.find((r: AdSetEntry) => r.id === currentId),
    [currentId, selectedAdSets],
  );

  const isSelected = !!currentRow;
  const isCBO = isSelected && currentRow.is_cbo;
  const isABO = isSelected && !currentRow.is_cbo;

  const ABO_AdSetIds = useMemo(
    () => selectedAdSets?.filter(adSet => !adSet.is_cbo).map(adSet => adSet.id) ?? [],
    [selectedAdSets],
  );

  const fieldName = useMemo(
    () => `selectedAdSetsBudgets.${currentId}`,
    [currentId],
  );

  return {
    selectedAdSets,
    currentRow,
    isSelected,
    isCBO,
    isABO,
    ABO_AdSetIds,
    fieldName,
    setValue,
    getValues,
  };
};

export const AdSetBudgetField: FC<AdSetBudgetFieldProps> = ({ cellParams }) => {
  const currentId: string = cellParams.row.id;

  const {
    selectedAdSets,
    currentRow,
    isSelected,
    isCBO,
    fieldName,
    setValue,
    getValues,
  } = useAdSetBudgetForm(currentId);

  useEffect(() => {
    if (!isSelected || isCBO) return;

    const currentValue: string = getValues(fieldName);
    const budgetValue = currentRow?.budget?.toString() ?? '';

    if (isUndefined(currentValue) && budgetValue) {
      setValue(fieldName, budgetValue, {
        shouldValidate: true,
      });
    }
  }, [getValues, setValue, isSelected, isCBO, fieldName, currentRow?.budget]);

  const handleApplyToAll = useCallback((): void => {
    const budgetToAll: string = getValues(fieldName) as string;
    const selectedAdSetsBudgetsValue: Record<string, string> = getValues(
      'selectedAdSetsBudgets',
    );

    if (!selectedAdSetsBudgetsValue || !budgetToAll) return;

    const values: Record<string, string> = Object.fromEntries(
      selectedAdSets
        .filter(adSet => !adSet.is_cbo)
        .map(({ id }) => [id, budgetToAll]),
    );

    setValue('selectedAdSetsBudgets', values, { shouldValidate: true });
  }, [selectedAdSets, setValue, getValues, fieldName]);

  const handleBoxClick = useCallback((e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
  }, []);

  const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    const newBudgetValue = event.target.value;

    if (!newBudgetValue) return;

    const selectedAdSetsBudgetsValue: Record<string, string> = getValues(
      'selectedAdSetsBudgets',
    );

    setValue('selectedAdSetsBudgets', {
      ...selectedAdSetsBudgetsValue,
      [currentId]: newBudgetValue,
    }, { shouldValidate: true });
  }, [getValues, setValue, currentId]);

  const { ref, inView } = useInView({
    threshold: 0,
  });

  const hasMultipleFlows = parseFlowsString(currentRow?.flow).length > 1;

  if (!isSelected) {
    return null;
  }

  if (isCBO) {
    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          gap: 1,
          width: '50%',
          display: 'flex',
          flexWrap: 'nowrap',
          justifyContent: 'start',
          alignItems: 'center',
        }}
      >
        <TextField
          disabled
          name={`selectedCampaignsBudgets.${currentRow?.campaign_id ?? ''}`}
          sx={{
            '&.MuiTextField-root': {
              background: 'transparent',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            },
            '& .MuiInputBase-root': {
              pr: 0.25,
              minWidth: 120,
              background: 'transparent',
            },
          }}
          InputProps={{
            type: 'number' as const,
            sx: {
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            },
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  sx={(t) => ({ '&': { fontSize: t.typography.caption.fontSize } })}
                >
                  CBO $
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        height: '100%',
        gap: 1,
        display: 'flex',
        flexWrap: 'nowrap',
        justifyContent: 'start',
        alignItems: 'center',
      }}
      onClick={handleBoxClick}
    >
      {inView && <>
        <TextField
          errorAsTooltip
          handleChange={handleChange}
          name={fieldName}
          sx={{
            '& .MuiInputBase-root': {
              pr: 0.25,
              minWidth: 160,
            },
          }}
          InputProps={{
            type: 'number' as const,
            sx: {
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            },
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  sx={(t) => ({ '&': { fontSize: t.typography.caption.fontSize } })}
                >
                  ABO $
                </Typography>
              </InputAdornment>
            ),
            endAdornment: hasMultipleFlows ? (
              <Typography
                noWrap
                sx={(t) => ({ '&': { minWidth: 56, fontSize: t.typography.caption.fontSize, color: t.palette.grey[500] } })}
              >
                per flow
              </Typography>
            ) : undefined,
          }}
        />
        {(selectedAdSets?.length ?? 0) > 1 && (
          <Box className={'MuiInputAdornment-positionEnd'}>
            <Button
              data-testid={'applyToAllBudgetCountButton'}
              onClick={handleApplyToAll}
              size={'small'}
              sx={{ textTransform: 'initial', px: 1 }}
              startIcon={<CopyAll />}
            >
              Apply to all
            </Button>
          </Box>
        )}
      </>}
    </Box>
  );
};

const arePropsEqual = (prevProps: AdSetBudgetFieldProps, nextProps: AdSetBudgetFieldProps): boolean => {
  const prevRow = prevProps.cellParams.row;
  const nextRow = nextProps.cellParams.row;

  return (
    prevRow.id === nextRow.id &&
    prevRow.budget === nextRow.budget &&
    prevRow.is_cbo === nextRow.is_cbo &&
    prevProps.cellParams.hasFocus === nextProps.cellParams.hasFocus
  );
};

export const MemoizedAdSetBudgetField = memo(
  AdSetBudgetField,
  (prevProps, nextProps) => arePropsEqual(prevProps, nextProps),
);